import * as Api from '@ViewModels';
import { EmailType } from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import { useState } from 'react';
import { ISingleEmailComposerComponentProps, SingleEmailComposerKey } from '../../../../../../../../models/AppState';
import { useUserSession } from '../../../../../../../../models/hooks/appStateHooks';
import {
	Dropdown,
	DropdownContent,
	DropdownEllipsisTrigger,
	DropdownItem,
} from '../../../../../../../components-v2/Dropdown';
import { THEME } from '../../../../../../../components-v2/Theme/theme';
import { Button } from '../../../../../../../components/Button';
import { InlineValueEditor } from '../../../../../../../components/entities/InlineValueEditor';
import { AIStarsIcon } from '../../../../../../../components/svgs/icons/AIStars';
import { EditItemPenIcon } from '../../../../../../../components/svgs/icons/EditItemPenIcon';
import { EmailIcon } from '../../../../../../../components/svgs/icons/EmailIcon';
import { PaperPlaneIcon } from '../../../../../../../components/svgs/icons/PaperPlaneIcon';
import { PrimaryIcon } from '../../../../../../../components/svgs/icons/PrimaryIcon';
import { TrashIcon } from '../../../../../../../components/svgs/icons/TrashIcon';
import { DeleteConfirmationPopover } from '../DeleteConfirmationPopover';
import { styleSheet } from '../../styles';

interface EmailCardProps extends ISingleEmailComposerComponentProps {
	email: Api.EmailAddress;
	isPrimary?: boolean;
	contact: Api.ContactViewModel;
	onEmailChanged: (email: Api.EmailAddress) => void;
	onMakePrimaryClicked: () => void;
	onDelete: () => void;
}
const _EmailCard = ({
	email,
	isPrimary = false,
	singleEmailComposer,
	contact,
	onEmailChanged,
	onMakePrimaryClicked,
	onDelete,
}: EmailCardProps) => {
	const userSession = useUserSession();
	const [isEditing, setIsEditing] = useState(false);
	const [emailValue, setEmailValue] = useState(email.value);
	const [isDeleting, setIsDeleting] = useState(false);
	const [isDropdownOpen, setIsDropdownOpen] = useState<boolean | null>(null);

	const updateEmail = () => {
		setIsEditing(false);
		onEmailChanged({ ...email, value: emailValue });
	};

	if (!email) {
		return null;
	}

	if (isEditing) {
		return (
			<InlineValueEditor
				autoComplete='off'
				autoFocus={true}
				compactLayout={true}
				id='entity-info-bullet-list-editor-input'
				onCancelButtonClicked={() => setIsEditing(false)}
				onChange={ev => setEmailValue(ev.target.value)}
				onKeyDown={ev => {
					if (ev.keyCode === 13) {
						updateEmail();
					}
				}}
				onSaveButtonClicked={updateEmail}
				value={emailValue || ''}
			/>
		);
	}

	return (
		<div className={css(styleSheet.emailCard)}>
			<div className={css(styleSheet.emailCardIcon)}>
				<EmailIcon fillColor={THEME.colors.border.main} width={18} height={18} />
			</div>
			<div className={css(styleSheet.emailCardContent)}>
				<div className={css(styleSheet.emailCardContentRow)}>
					<div>
						<a className={css(styleSheet.emailCardValue)} href={`mailto:${email.value}`}>
							{email.value}
						</a>
						{isPrimary ? (
							<div className={css(styleSheet.emailCardPrimaryLabel)}>
								<PrimaryIcon />
								<label>Primary Email</label>
							</div>
						) : null}
					</div>
					<div>
						<Button
							kind='icon'
							className={css(styleSheet.emailCardActionButton)}
							onClick={() => {
								singleEmailComposer?.showForRecipients([
									new Api.ContactViewModel(userSession, {
										...contact.toJs(),
										primaryEmail: email,
									}),
								]);
							}}
						>
							<PaperPlaneIcon style={{ color: THEME.colors.primary.main }} />
						</Button>
					</div>

					<DeleteConfirmationPopover
						open={isDeleting}
						onOpenChange={setIsDeleting}
						onConfirm={onDelete}
						message={<>Do you want to delete the email address {email.value}?</>}
					>
						<div>
							<Dropdown onOpenChange={open => setIsDropdownOpen(open)} open={isDropdownOpen}>
								<DropdownEllipsisTrigger />
								<DropdownContent styles={[styleSheet.emailCardDropdownContent]}>
									<DropdownItem
										onClick={() => {
											singleEmailComposer?.showForRecipientWithAI(
												[
													new Api.ContactViewModel(userSession, {
														...contact.toJs(),
														primaryEmail: email,
													}),
												],
												EmailType.Generic
											);
										}}
									>
										<AIStarsIcon />
										Craft Email with AI
									</DropdownItem>
									{!isPrimary && (
										<DropdownItem onClick={onMakePrimaryClicked}>
											<PrimaryIcon />
											Make Primary Address
										</DropdownItem>
									)}
									<DropdownItem onClick={() => setIsEditing(true)}>
										<EditItemPenIcon /> Edit
									</DropdownItem>
									<DropdownItem onClick={() => setIsDeleting(true)}>
										<div className={css(styleSheet.emailCardDeleteButton)}>
											<TrashIcon />
											<span>Delete</span>
										</div>
									</DropdownItem>
								</DropdownContent>
							</Dropdown>
						</div>
					</DeleteConfirmationPopover>
				</div>
			</div>
		</div>
	);
};

const EmailCardWithObserver = observer(_EmailCard);
export const EmailCard = inject(SingleEmailComposerKey)(EmailCardWithObserver);
